import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Image as ImageIcon, Video, Upload, Loader2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_VIDEO_DURATION = 15; // seconds
const MAX_FILE_SIZE_MB = 10;
const IMAGE_DURATION = 7; // seconds

export default function CreateStoryModal({ user, onClose, onStoryCreated }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setError(null);
    
    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      setError(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB`);
      return;
    }
    
    // Determine media type
    if (file.type.startsWith('image/')) {
      setMediaType('image');
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setVideoDuration(IMAGE_DURATION);
    } else if (file.type.startsWith('video/')) {
      // Validate video duration
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        
        if (video.duration > MAX_VIDEO_DURATION) {
          setError(`Video must be ${MAX_VIDEO_DURATION} seconds or less. Your video is ${Math.ceil(video.duration)}s`);
          return;
        }
        
        setMediaType('video');
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setVideoDuration(Math.ceil(video.duration));
      };
      
      video.onerror = () => {
        setError('Could not load video. Please try a different file.');
      };
      
      video.src = URL.createObjectURL(file);
    } else {
      setError('Unsupported file type. Please select an image or video.');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/stories`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      toast.success('Darshan posted!');
      if (onStoryCreated) {
        onStoryCreated(response.data);
      }
      onClose();
    } catch (error) {
      console.error('Story upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create darshan');
    } finally {
      setUploading(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setMediaType(null);
    setVideoDuration(0);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div 
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
        data-testid="create-story-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Create Darshan</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-story-modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* User Info */}
          <div className="flex items-center gap-3 mb-4">
            <img
              src={user?.profile_photo_url || user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=FF9933&color=fff`}
              alt={user?.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <p className="font-semibold text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">Your darshan • Visible for 24 hours</p>
            </div>
          </div>

          {/* File Selection */}
          {!previewUrl ? (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="story-file-input"
              />
              
              {/* Upload Area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#FF9933] hover:bg-orange-50/30 transition-colors"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#FF9933] to-[#FF6600] rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-white" />
                </div>
                <p className="font-medium text-gray-700 mb-1">Upload Photo or Video</p>
                <p className="text-sm text-gray-500">
                  Images: Auto display for {IMAGE_DURATION}s
                </p>
                <p className="text-sm text-gray-500">
                  Videos: Max {MAX_VIDEO_DURATION}s duration
                </p>
              </div>

              {/* Quick Options */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    fileInputRef.current?.setAttribute('accept', 'image/*');
                    fileInputRef.current?.click();
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl transition-colors"
                >
                  <ImageIcon className="w-5 h-5" />
                  <span className="font-medium">Photo</span>
                </button>
                <button
                  onClick={() => {
                    fileInputRef.current?.setAttribute('accept', 'video/*');
                    fileInputRef.current?.click();
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl transition-colors"
                >
                  <Video className="w-5 h-5" />
                  <span className="font-medium">Video</span>
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-xl">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
            </div>
          ) : (
            /* Preview */
            <div className="space-y-4">
              <div className="relative bg-black rounded-xl overflow-hidden aspect-[9/16] max-h-[400px]">
                {mediaType === 'image' ? (
                  <img
                    src={previewUrl}
                    alt="Story preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <video
                    ref={videoRef}
                    src={previewUrl}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay
                    muted
                    loop
                  />
                )}
                
                {/* Remove Button */}
                <button
                  onClick={clearSelection}
                  className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                
                {/* Duration Badge */}
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded-lg flex items-center gap-1">
                  <Clock className="w-3 h-3 text-white" />
                  <span className="text-white text-xs">{videoDuration}s</span>
                </div>
                
                {/* Type Badge */}
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 rounded-lg flex items-center gap-1">
                  {mediaType === 'image' ? (
                    <ImageIcon className="w-3 h-3 text-green-400" />
                  ) : (
                    <Video className="w-3 h-3 text-red-400" />
                  )}
                  <span className="text-white text-xs capitalize">{mediaType}</span>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Uploading...</span>
                    <span className="text-[#FF9933] font-medium">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#FF9933] to-[#FF6600] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={clearSelection}
                  disabled={uploading}
                  className="flex-1"
                >
                  Change
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-gradient-to-r from-[#FF9933] to-[#FF6600] hover:from-[#E68A00] hover:to-[#CC5500] text-white"
                  data-testid="post-story-btn"
                >
                  {uploading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Posting...
                    </span>
                  ) : (
                    'Post Darshan'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="px-4 pb-4">
          <p className="text-xs text-center text-gray-400">
            Darshan disappear after 24 hours. Max 5 minutes total per day.
          </p>
        </div>
      </div>
    </div>
  );
}
